/**
 * ConfigManager - Validates and manages Watchdog configuration
 */

import type { WatchdogConfig } from '../types.js';

export class ConfigManager {
  private config: Required<WatchdogConfig>;

  constructor(userConfig: WatchdogConfig) {
    this.config = this.validateAndMergeConfig(userConfig);
  }

  /**
   * Validate required fields and merge with defaults
   */
  private validateAndMergeConfig(userConfig: WatchdogConfig): Required<WatchdogConfig> {
    // Validate required fields
    if (!userConfig.apiKey) {
      throw new Error('Watchdog initialization failed: apiKey is required');
    }
    if (!userConfig.projectId) {
      throw new Error('Watchdog initialization failed: projectId is required');
    }
    if (!userConfig.endpoint) {
      throw new Error('Watchdog initialization failed: endpoint is required');
    }

    // Validate endpoint format
    try {
      new URL(userConfig.endpoint);
    } catch (error) {
      throw new Error('Watchdog initialization failed: endpoint must be a valid URL');
    }

    // Validate optional sessionRecordingEndpoint if provided
    if (userConfig.sessionRecordingEndpoint) {
      try {
        new URL(userConfig.sessionRecordingEndpoint);
      } catch (error) {
        throw new Error('Watchdog initialization failed: sessionRecordingEndpoint must be a valid URL');
      }
    }

    // Merge with defaults
    return {
      // Required (already validated)
      apiKey: userConfig.apiKey,
      projectId: userConfig.projectId,
      endpoint: userConfig.endpoint,

      // Optional with defaults
      userId: userConfig.userId || null,
      batchSize: userConfig.batchSize ?? 25,
      flushInterval: userConfig.flushInterval ?? 15000,
      debug: userConfig.debug ?? false,

      // Feature toggles (all enabled by default)
      enableWebVitals: userConfig.enableWebVitals ?? true,
      enableErrorTracking: userConfig.enableErrorTracking ?? true,
      enablePerformanceTracking: userConfig.enablePerformanceTracking ?? true,
      enableBehaviorTracking: userConfig.enableBehaviorTracking ?? true,
      enableBusinessMetrics: userConfig.enableBusinessMetrics ?? true,
      enableSecurityTracking: userConfig.enableSecurityTracking ?? true,
      enableSessionRecording: userConfig.enableSessionRecording ?? true,

      // Privacy settings
      enablePrivacyMode: userConfig.enablePrivacyMode ?? true,
      sensitiveSelectors: userConfig.sensitiveSelectors ?? [
        '.sensitive',
        '[data-sensitive]',
        '.password',
        '.ssn',
        '.credit-card',
        '.personal-info'
      ],
      maskAllInputs: userConfig.maskAllInputs ?? true,

      // Session recording
      sessionRecordingEndpoint: userConfig.sessionRecordingEndpoint ?? undefined,
      maxRecordingTime: userConfig.maxRecordingTime ?? 1800000, // 30 minutes
      segmentDuration: userConfig.segmentDuration ?? 30000, // 30 seconds
      maxSegmentSize: userConfig.maxSegmentSize ?? 500,
      compressSegments: userConfig.compressSegments ?? false,

      // Error handling
      maxStackTraceLength: userConfig.maxStackTraceLength ?? 1000,
      maxErrorMessageLength: userConfig.maxErrorMessageLength ?? 500,
      sendCriticalErrorsImmediately: userConfig.sendCriticalErrorsImmediately ?? true,
      criticalErrorDelay: userConfig.criticalErrorDelay ?? 0
    } as Required<WatchdogConfig>;
  }

  /**
   * Get configuration value
   */
  get<K extends keyof WatchdogConfig>(key: K): Required<WatchdogConfig>[K] {
    return this.config[key];
  }

  /**
   * Get all configuration
   */
  getAll(): Required<WatchdogConfig> {
    return { ...this.config };
  }

  /**
   * Update configuration (for runtime changes like userId)
   */
  update<K extends keyof WatchdogConfig>(key: K, value: WatchdogConfig[K]): void {
    (this.config as any)[key] = value;
  }

  /**
   * Check if a feature is enabled
   */
  isEnabled(feature: keyof Pick<WatchdogConfig, 
    'enableWebVitals' | 
    'enableErrorTracking' | 
    'enablePerformanceTracking' | 
    'enableBehaviorTracking' | 
    'enableBusinessMetrics' | 
    'enableSecurityTracking' | 
    'enableSessionRecording'
  >): boolean {
    return this.config[feature] === true;
  }
}

